"use client";

import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";

const FEED_PREFETCH_MARGIN = "500px 0px";

export function InfiniteFeedLoader({
  hasMore,
  isLoading,
  error,
  noun = "updates",
  onLoadMore,
}: {
  hasMore: boolean;
  isLoading: boolean;
  error?: Error | null;
  /** What the list holds, for the loader's own wording. */
  noun?: string;
  onLoadMore: () => void;
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoading || error) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMoreRef.current();
        }
      },
      { rootMargin: FEED_PREFETCH_MARGIN },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [error, hasMore, isLoading]);

  if (!hasMore) return null;

  return (
    <div
      ref={sentinelRef}
      className="flex h-12 shrink-0 items-center justify-center"
      aria-live="polite"
    >
      {error ? (
        <Button type="button" variant="outline" size="sm" onClick={onLoadMore}>
          Could not load older {noun}. Retry
        </Button>
      ) : isLoading ? (
        <p className="sr-only">Loading older {noun}…</p>
      ) : (
        <button
          type="button"
          className="sr-only focus:not-sr-only focus:rounded-md focus:px-3 focus:py-2 focus:text-xs"
          onClick={onLoadMore}
        >
          Load older {noun}
        </button>
      )}
    </div>
  );
}
